<!-- The generic component `donut-input-async-number` is based heavily on this previous iteration. -->
<!-- However, the unique aspects of this component (default values when nil, changing values from roles) -->
<!-- are such that I left this one as it's own, similar thing. Use `donut-input-async-number` for general use. -->
<template>
  <div :class="isLoading ? 'async-number pe-n o-50' : 'async-number'">
    <input
      v-if="isOpen"
      ref="inputEl"
      v-model="allowanceInput"
      type="number"
      :step="1"
      :min="0"
      :max="999"
      @blur="onBlur"
      @keydown.enter.prevent="onTabOrEnter"
      @keydown.tab.prevent="onTabOrEnter"
    >
    <div v-else :class="`f-i f-ac b-1 br-4 p-x1 c-p ${textClasses}`" @click="onClick">
      <div class="t">{{ allowance }}</div>
      <donut-upgrade-tag v-if="upgradeRequired" class="m-lx1" />
    </div>
    <Transition name="status-icon">
      <div v-if="statusIcon" class="pos-abs top-8 right-0">
        <i v-if="statusIcon === 'loading'" class="mi anim-spin purple">refresh</i>
        <i v-if="statusIcon === 'success'" class="mi green">check</i>
        <i v-if="statusIcon === 'error'" class="mi red">close</i>
      </div>
    </Transition>
  </div>
</template>

<script setup>
  import { ref, computed, nextTick } from 'vue';
  import { useStore } from 'vuex';
  import Network from 'lib/api/network';

  const props = defineProps({
    user: { type: Object, required: true },
    paywall: { type: Object, default: null },
  });
  const emit = defineEmits(['advance']);
  const { state, getters, commit, dispatch } = useStore();

  const asyncValues = computed(() => state.pointsLeaderboard.asyncValuesByUser[props.user.id]);
  const allowance = computed(() => asyncValues.value.allowance);
  const isCustom = computed(() => asyncValues.value.isCustom);
  const upgradeRequired = computed(() => getters.upgradeRequired(props.paywall));

  // Elements
  const inputEl = ref(null);
  const textClasses = computed(() => {
    if (isCustom.value) return 'bc-light-gray';
    return 'light-gray';
  });

  // Input Status
  const allowanceInput = ref(allowance.value);
  const isOpen = ref(false);
  const isLoading = ref(false);
  const statusIcon = ref(null);

  // Submit Data
  const delayedClearStatusIcon = () => {
    setTimeout(() => {
      isLoading.value = false;
      statusIcon.value = null;
    }, 500);
  };
  const submit = () => {
    isOpen.value = false;
    // Only do anything if the value changed
    if (allowanceInput.value !== allowance.value) {
      isLoading.value = true;
      statusIcon.value = 'loading';
      // Account for immediate reasons why this might fail and don't send request if so
      if (allowanceInput.value) {
        let immediateFailReason;
        if (allowanceInput.value !== parseInt(allowanceInput.value)) immediateFailReason = 'Allowance must be an integer or left blank.';
        if (allowanceInput.value > 999) immediateFailReason = 'Allowance must not exceed 999.';
        if (allowanceInput.value < 0) immediateFailReason = 'Allowance can not be a negative number.';
        if (immediateFailReason) {
          statusIcon.value = 'error';
          delayedClearStatusIcon();
          dispatch('setErrorToast', immediateFailReason);
          return;
        }
      }
      // Make a network request to update the value
      Network.put(`/points/wallets/${props.user.id}`, { allowance: allowanceInput.value }, {
        success: (data) => {
          statusIcon.value = 'success';
          commit('pointsLeaderboard/updateAsyncValuesForUser', { id: props.user.id, key: 'allowance', value: data.allowance, markColOutdated: true });
          commit('pointsLeaderboard/updateAsyncValuesForUser', { id: props.user.id, key: 'isCustom', value: data.isCustom });
        },
        error: (error) => {
          statusIcon.value = 'error';
          allowanceInput.value = props.user.points.allowance;
        },
        complete: () => { delayedClearStatusIcon(); },
      });
    }
  };

  // Focusing
  const open = () => {
    if (!isLoading.value) {
      isOpen.value = true;
      nextTick(() => {
        // Wait for the inputEl to render once isOpen is true
        inputEl.value.focus();
        // If the allowance value changed from a role update, we want to reflect that newer value when we open up the input
        allowanceInput.value = allowance.value;
      });
    }
  };
  defineExpose({ open }); // Allow the parent to access this so that it can focus on the next input when we emit('advance')

  // Interactions
  const onClick = () => {
    if (upgradeRequired.value) {
      dispatch('openUpgradeModalIfRequired', props.paywall);
    } else {
      open();
    }
  };
  const onBlur = () => {
    submit();
  };
  const onTabOrEnter = () => {
    // Don't call submit directly here, because doing so will set isOpen to false, thus triggering
    // onBlur which will call submit again. Instead, just let these keypresses blur the input and
    // let onBlur handle that. emit('advance') tells the parent table to focus on the next row's component.
    inputEl.value.blur();
    emit('advance');
  };
</script>
